import { memo, useRef } from 'react';
import { TagChip } from '@/components/TagDisplay';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';

interface FilterBarProps {
	activeTagFilters: string[];
	onRemoveTagFilter: (tagName: string) => void;
	onClearAllFilters: () => void;
	searchText: string;
	onSearchTextChange: (text: string) => void;
	onOpenTagSearch: () => void;
	resultCount: number;
}

export const FilterBar = memo(function FilterBar({
	activeTagFilters,
	onRemoveTagFilter,
	onClearAllFilters,
	searchText,
	onSearchTextChange,
	onOpenTagSearch,
	resultCount,
}: FilterBarProps) {
	const inputRef = useRef<HTMLInputElement>(null);
	const hasFilters = activeTagFilters.length > 0 || searchText.trim().length > 0;

	return (
		<div className="flex items-center gap-2 px-4 py-2 border-b border-border bg-card/50">
			{/* Tag filter chips */}
			{activeTagFilters.length > 0 && (
				<div className="flex items-center gap-1.5 flex-wrap">
					{activeTagFilters.map((tagName) => (
						<TagChip
							key={tagName}
							tag={{
								id: 0,
								name: tagName,
								color: null,
								icon: null,
							}}
							size="md"
							onRemove={() => onRemoveTagFilter(tagName)}
						/>
					))}
				</div>
			)}

			{/* Divider between tags and search */}
			{activeTagFilters.length > 0 && (
				<div className="h-5 w-px bg-border" />
			)}

			{/* Search input */}
			<div className="relative flex-1 max-w-sm">
				<span className="absolute left-2 top-1/2 -translate-y-1/2 text-muted-foreground font-mono text-sm">&gt;</span>
				<Input
					ref={inputRef}
					type="text"
					placeholder="search:"
					value={searchText}
					onChange={(e) => onSearchTextChange(e.target.value)}
					className="pl-6 pr-16 h-8 text-sm font-mono"
				/>
				{/* CTRL+K hint */}
				<button
					type="button"
					onClick={onOpenTagSearch}
					className="absolute right-2 top-1/2 -translate-y-1/2 text-xs text-muted-foreground hover:text-primary px-1.5 py-0.5 border border-border bg-background hover:border-primary font-mono"
				>
					⌘K
				</button>
			</div>

			{/* Result count */}
			<span className="text-xs text-muted-foreground whitespace-nowrap font-mono">
				[{resultCount} {resultCount === 1 ? 'link' : 'links'}]
			</span>

			{/* Clear all button */}
			{hasFilters && (
				<Button
					variant="outline"
					size="sm"
					onClick={onClearAllFilters}
					className="h-7 px-2 text-xs font-mono"
				>
					[CLEAR]
				</Button>
			)}
		</div>
	);
});
